import React from 'react';
import {FormattedMessage} from 'react-intl';
import contactUsUrl from 'utils/url/contactUsUrl';
import styles from './faq.scss';

const contactUsLink = <a className={styles.faqItemLink} href={contactUsUrl()} target="_blank"><FormattedMessage id="common.link.contactUs.text"/></a>;
const documentsPicture = <span className={styles.documentsIcon} />;

const paymentPlanImpactByByEarlyPayment = (
  <div className={styles.faqBulletedList}>
    <div>We offer 3 convenient early payment options:</div>
    <ul>
      <li>
        <span className={styles.faqItemTextHelperBold}>Make a one time payment - Apply to Next Payments: </span>
        this option enables you to pay any desired amount (minimum of $2 up to the entire outstanding balance). If you select this option, the payment amount will automatically be deducted from the <span className={styles.faqItemTextHelperBoldAndUnderline}>next payment(s)</span>.
      </li>
      <li>
        <span className={styles.faqItemTextHelperBold}>Make a one-time payment - Apply to Last Payments:</span>
        this option enables you to pay any desired amount (minimum of $2 up to the entire outstanding balance). <span className={styles.faqItemTextHelperBoldAndUnderline}> Note, this payment will automatically be deducted from the last payment(s). It will not affect any automated debits that you have set up and you will still owe your next scheduled payment on its due date.</span>
      </li>
      <li>
        <span className={styles.faqItemTextHelperBold}>Pay entire outstanding amount: </span>
        by selecting this option you will pay the full outstanding balance (as of your payment date) and will have no additional payments to make. Paying off early will decrease the amount of finance charges you will end up paying.
      </li>
    </ul>
    <div>
      When you select any of these options an updated payment schedule will be displayed so you can clearly see how your payment will impact your other outstanding payments. If you are unable to process any payments, please {contactUsLink}
    </div>
  </div>
);

const faqData = [
  {
    "title": "PAYMENTS & FEES",
    "items": [
      {
        "question": "When are my payments due?",
        "answer": "The payment schedule, which was provided to you in your financing agreement (loan agreement or retail installment contract), lists all your payment due dates and payment amounts. If you authorized automatic payments, we will initiate charges on or after the scheduled due dates, as detailed in your financing agreement.",
      },
      {
        "question": "Can I change my payment due dates? Will I be charged for making this change?",
        "answer": <span>You can change the due dates of your payment schedule to a different day. For example, if your payments are due on the 10<sup>th</sup> of each month you can move the payment due dates to the 20<sup>th</sup>  (or any other day you prefer). Please note, that you can only change these dates once during the term, or lifetime, of your financing agreement (loan agreement or retail installment contract). You will not be charged for changing the dates as described above.  If you need any further assistance, please {contactUsLink}.</span>,
      },
      {
        "question": "How often can I change my payment due dates?",
        "answer": <span>You can make one change to the due date of your loan agreement or retail installment contract. Please note that when you change the date it will apply to all of your future payment dates. If you need any further assistance, please {contactUsLink}.</span>,
      },
      {
        "question": "Are there any fees for an early payment?",
        "answer": "No, there are no prepayment penalties or fees for early payment. In fact, making early or additional payments may allow you to pay lower finance charges under the agreement."
      },
      {
        "question": "How will my payment schedule be impacted if I make an early payment?",
        "answerType": "component",
        "answer": paymentPlanImpactByByEarlyPayment
      },
      {
        "question": "Can I update my debit card details?",
        "answer": <span>You can easily update the debit card used for your recurring debit card authorization by clicking on the "Update Debit Card" tab ("Card" on mobile devices) on your purchase window.</span>
      },
    ]
  },
  {
    "title": "MY PURCHASES",
    "items": [
      {
        "question": <span>Why do some payments have numbers (1st, 2nd…) while others are "manual"?</span>,
        "answer": "Your financing agreement (loan agreement or retail installment contract) contains a Payment Schedule with all your scheduled payments. Those payments are numbered. A manual payment is any other payment which you initiated at a later stage either by phone or online."
      },
      {
        "question": "Why is my purchase showing a \"Hold\" or \"Dispute\" status?",
        "answer": <span>These statuses reflect certain actions which have been initiated with respect to your financing agreement. Please {contactUsLink} with any questions regarding these purchases.</span>
      },
      {
        "question": "Where can I find the legal documents pertaining to my purchase?",
        "answer": <span>
          Each one of your purchases has an icon at the top right {documentsPicture} which will direct you to the agreements, policies and consents associated with your purchase.
          </span>
      },
      {
        "question": "How are returns/refunds processed?",
        "answer": "Please contact the merchant to return a purchase. Items purchased are eligible for return to the merchant in accordance with the merchant’s return policy. If you made any down payment directly to the merchant, the merchant will issue you a refund for that amount. Sunbit will refund any payments that have been made to or processed by us, on behalf of the bank or the merchant. Refunds issued by us will be issued in the same form of payment that you used to pay us and may take up to 5 or more business days to post to your account, depending on your bank or card issuer."
      },
      {
        "question": "Who do I contact with any other questions relating to a purchase or payments?",
        "answer": <span>If you have any other questions or issues related to your purchase or payments, please {contactUsLink}.</span>
      },
      {
        "question": "Why am I unable to process my payment online?",
        "answer": <span>In some instances, our system is unable to process an online payment on the Sunbit portal. If you are unable to process a payment, please {contactUsLink}.</span>
      }
    ]
  },
  {
    "title": "PROMOTIONAL OFFERS",
    "items": [
      {
        "question": <span>What is the "No Finance Charges If Paid in Full Within 1 Month" offer?</span>,
        "answer": (<div>
          If you pay the amount financed in full within 1 month of your purchase, then you will not pay any finance charges. If you do not pay the amount financed in full by that time, then you will be responsible for paying all finance charges that have accrued and will accrue from the date of your agreement.
          <br/>
          <br/>
          As an example, if you made a purchase on 1/1 and your promotional period ends on 2/1, then you will not have to pay any finance charges if you pay off the amount financed in full by 11:59 PM PT on the 2/1.
          You can see when your promotional offer ends on your financing agreement or by logging in to <a href="https://mysunbit.com" target="_blank">mysunbit.com</a>. This option is not available on all products.
          </div>)
      },
      {
        "question": <span>Where can I find my "amount financed"?</span>,
        "answer": <span>The "amount financed" is detailed in your financing agreement (loan agreement or retail installment contract). See the table on page 1 of the financing agreement titled "Truth in Lending Disclosure".</span>
      },
      {
        "question": <span>What if I made a payment during the first month (before paying off the entire purchase)?</span>,
        "answer": (<div>
          Any payment made during the first month as a scheduled payment, an additional "manual" payment or by moving a scheduled payment to an earlier date will be applied towards the total amount required to pay off the purchase. Either way, you will not pay any finance charges if you pay the amount financed in full during the first month.
          <br/><br/>
          If any payment you made during the first month included a finance charge component, it will be applied towards your principal if you pay the amount financed in whole within the first month.
          </div>)
      },
      {
        "question": <span>Will this promotion have any other effect on my purchase or payment schedule?</span>,
        "answer": <span>No. This promotion will not affect your payment schedule in any other way. If you do not take advantage of this offer, you will be responsible for paying based on the agreed terms detailed in your financing agreement.</span>
      },
    ]
  },
  {
    "title": "GENERAL QUESTIONS",
    "items": [
      {
        "question": "Can I finance additional purchases using Sunbit technology?",
        "answer": "Every time you apply for financing using Sunbit technology, your eligibility is determined accordingly. If you qualify for financing, you will see financing options available to you."
      },
      {
        "question": "Why was my application denied?",
        "answer": <div className={styles.faqBulletedList}>
            <ul>
              <li>If you apply for retail installment financing from a merchant and the merchant is unable to extend credit to you, you will receive a notification by email with information on the denial, including how to obtain the specific reasons for the denial.  Please refer to this notice for more information.</li>
              <li>If you apply for an installment loan from TAB Bank and TAB Bank is unable to extend credit to you, you will receive a notification by email with information on the denial, including how to obtain the specific reasons for the denial. Please refer to this notice for more information.</li>
          </ul>
        </div>
      },
      {
        "question": "Who is TAB Bank and how is TAB Bank related to Sunbit?",
        "answer": <span>Transportation Alliance Bank, dba TAB Bank, is a Utah state-chartered bank. TAB Bank determines qualifications for and terms of credit for loans offered through the tablet. Only customers whose financing agreement is titled, "Installment Loan Agreement," have loans financed by TAB Bank.</span>
      },
      {
        "question": "Can I finance my purchase using Sunbit technology at any store?",
        "answer": <span>Sunbit technology is currently limited to select retailers and service providers.</span>
      },
    ]
  }
];

export default faqData;



// WEBPACK FOOTER //
// ./src/faq/faqData.js